import { useEffect, useRef, useState } from "react";
import { fetchAttachmentBlob } from "@/api/taskAttachments";

/**
 * D-141.G G6 (B2-13) — спільний blob-хук для secured-вкладень.
 *
 * Режими:
 *  - lazy (list-Thumb): blob тягнеться ЛИШЕ коли елемент реально видимий —
 *    IntersectionObserver на `ref`-елементі; 10 фото на записі ≠ 10 запитів
 *    на mount, довантаження при скролі.
 *  - eager (Viewer, П-14): модалка видима за визначенням — жодного IO-гейта,
 *    fetch одразу на mount (0 тіків порожнечі понад мережевий час).
 *
 * Lifecycle byte-parity зі старими inline-ефектами (П-15, БЕЗ нового кеша):
 * fetch → createObjectURL → revoke на cleanup; re-open = повторний fetch —
 * так було і до G6.
 */
export function useAttachmentBlob(
  url: string,
  {
    lazy = false,
    enabled = true,
    errorFallback = "Не вдалося завантажити файл",
    blobType,
  }: {
    lazy?: boolean;
    enabled?: boolean;
    errorFallback?: string;
    /**
     * D-146 W2 fix: force the blob MIME before createObjectURL. Chrome's PDF
     * viewer inside an <iframe src=blob:> never finishes loading (endless
     * plugin throbber) when the blob type is empty/octet-stream — re-wrapping
     * with the explicit type removes that failure class.
     */
    blobType?: string;
  } = {},
) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(!lazy);
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!lazy || visible || !enabled) return;
    const el = ref.current;
    if (!el) return;
    // Без IO (дуже старий браузер/jsdom) — деградація до eager, не до «ніколи».
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setVisible(true);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [lazy, visible, enabled]);

  useEffect(() => {
    if (!enabled || !visible) return;
    let obj: string | null = null;
    let active = true;
    setError(null);
    setSrc(null);
    fetchAttachmentBlob(url)
      .then((blob) => {
        if (!active) return;
        const typed = blobType && blob.type !== blobType ? new Blob([blob], { type: blobType }) : blob;
        obj = URL.createObjectURL(typed);
        setSrc(obj);
      })
      .catch((e: unknown) => {
        if (!active) return;
        setError(e instanceof Error ? e.message : errorFallback);
      });
    return () => {
      active = false;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [url, enabled, visible, errorFallback, blobType]);

  return { ref, src, error, visible };
}
